import {store} from './../../plugins/vuex/index'
import moment from 'moment';
import i18nHelper from "@/core/services/i18n.service";
import striptags from "striptags";

class Formatter {
  static formatNumber(number, decimals, dec_point, thousands_sep) {
    // https://stackoverflow.com/questions/3753483/javascript-thousand-separator-string-format/19840881
    const n = !isFinite(+number) ? 0 : +number;
    const prec = !isFinite(+decimals) ? 0 : Math.abs(decimals);
    const sep = (typeof thousands_sep === 'undefined') ? ',' : thousands_sep;
    const dec = (typeof dec_point === 'undefined') ? '.' : dec_point;
    const toFixedFix = function (n, prec) {
      // Fix for IE parseFloat(0.55).toFixed(0) = 0;
      const k = Math.pow(10, prec);
      return Math.round(n * k) / k;
    };
    const s = (prec ? toFixedFix(n, prec) : Math.round(n)).toString().split('.');
    if (s[0].length > 3) {
      s[0] = s[0].replace(/\B(?=(?:\d{3})+(?!\d))/g, sep);
    }
    if ((s[1] || '').length < prec) {
      s[1] = s[1] || '';
      s[1] += new Array(prec - s[1].length + 1).join('0');
    }
    return s.join(dec);
  }

  static getParsedNumber(value) {
    value = parseFloat(value);

    let decimal_separator = '.',
      thousand_separator = ',';

    if (store.state.global.currentBranch) {
      decimal_separator = store.state.global.currentBranch.decimal_separator;
      thousand_separator = store.state.global.currentBranch.thousand_separator;
    }

    return this.formatNumber(
      value,
      value % 1 === 0 ? 0 : store.state.global.formats.precisionCount,
      decimal_separator,
      thousand_separator
    );
  }

  static asPercentage(value) {
    return this.getParsedNumber(value) + '%';
  }

  static asCurrency(value, currency) {
    if (!['number', 'string'].includes(typeof value)) {
      console.error(`Method asCurrency expects value to be 'number', but '${typeof value}' given`);
    } else {
      if (typeof value === 'number' && value < 0) {
        return '-' + (currency || store.state.global.currentBranch.currency.symbol) + this.getParsedNumber(value * -1);
      }
      return (currency || store.state.global.currentBranch.currency.symbol) + this.getParsedNumber(value);
    }
  }

  static removeUnderscores(value) {
    if (!value) return '';
    return value.replace(/_/g, ' ');
  }

  static removeDashes(value) {
    if (!value) return '';
    return value.replace(/-/g, ' ');
  }

  static asCurrencyByCode(value, currencyCode) {
    if (typeof value !== 'number') {
      console.error(`Method asCurrency expects value to be 'number', but '${typeof value}' given`);
    } else {
      const currencyObj = store.state.global.currencies.find(curr => curr.code === currencyCode);
      if (!currencyObj) {
        return value;
      } else {
        return this.asCurrency(value, currencyObj.symbol);
      }
    }
  }

  static toDate(value, format) {
    if (format) {
      return moment(value).format(format);
    } else if (store.state.global && store.state.global.currentBranch) {
      return moment(value).format(store.state.global.currentBranch.date_format);
    } else {
      return moment(value).format('DD-MM-YYYY hh:mm');
    }
  }

  static toDateTime(value, format) {
    if (format) {
      return moment(value).format(format);
    } else if (store.state.global && store.state.global.currentBranch) {
      return moment(value).format(`${store.state.global.currentBranch.date_format} ${store.state.global.currentBranch.time_format}`)
    } else {
      return moment(value).format('DD-MM-YYYY hh:mm');
    }
  }

  static getNowAsDate() {
    return moment().format(SYSTEM_DATE_FORMAT);
  }

  static getNowAsTime(resetSeconds = true) {
    if (resetSeconds) {
      return moment().seconds(0).format(SYSTEM_TIME_FORMAT);
    }
    return moment().format(SYSTEM_TIME_FORMAT);
  }

  static toSlug(value) {
    return value.toString().toLowerCase()
      .replace(/\s+/g, '-')           // Replace spaces with -
      .replace(/[^\w-]+/g, '')       // Remove all non-word chars
      .replace(/--+/g, '-')         // Replace multiple - with single -
      .replace(/^-+/, '')             // Trim - from start of text
      .replace(/-+$/, '');            // Trim - from end of text
  }

  static toUpperCaseFirst(value) {
    return value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
  }

  static getByMasterLanguage(value) {
    const masterLanguage = i18nHelper.getMasterLanguage();
    if (!masterLanguage || !value || !value[masterLanguage.key]) {
      return '';
    }
    return value[masterLanguage.key];
  }

  static attributeByMasterLanguage(value, attr) {
    const masterLangVal = this.getByMasterLanguage(value);
    if (!masterLangVal || !masterLangVal[attr]) {
      return '';
    }
    return masterLangVal[attr];
  }

  static getCurrentDate(format = null) {
    return this.toDate(new Date(), format ? format : SYSTEM_DATE_FORMAT)
  }

  static getDateAndTimeToSystemFormat(date, time) {
    let currentTzStartDate = moment(`${date} ${time}`);
    let utcStartDate = moment(currentTzStartDate.toISOString()).utc();
    return `${utcStartDate.format(SYSTEM_DATE_FORMAT)} ${utcStartDate.format(SYSTEM_TIME_FORMAT)}`;
  }

  static getCountryByCode(code) {
    if (store.state && store.state.global && store.state.global.countriesMap) {
      return store.state.global.countriesMap[code] || null
    }
    return null
  }

  static toUpperCaseEveryWord(value) {
    const splitValue = value.toLowerCase().split(' ');
    return splitValue.map(sv => sv.charAt(0).toUpperCase() + sv.substring(1)).join(' ');
  }

  static stripTags(value) {
    if (!value) {
      return value;
    }

    const obj = {
      "&nbsp;": " ",
      "&amp;": "&",
      "&quot;": "\"",
      "&lt;": "<",
      "&gt;": ">"
    };
    //striptags() returns empty string if the argument type is number, so value needs to be converted toString
    value = striptags(value.toString());

    Object.keys(obj).forEach((key) => {
      value = value.replace(new RegExp(key, 'g'), obj[key]);
    });

    return value;
  }

  static truncate(value, length) {
    if (!value) return '';
    if (!length || value.length < length) {
      return value;
    }
    return `${value.substr(0, length)}...`;
  }
}

export {
  Formatter
};
export const SYSTEM_DATE_FORMAT = 'YYYY-MM-DD';
export const SYSTEM_TIME_FORMAT = 'HH:mm:ss';
